/**
 * Dashboard Service
 * Provides aggregated data for the admin dashboard from multiple Firestore collections
 */

import {
  collection,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  getDocs,
  Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import { Lead, LeadActivity } from '../types/lead';
import { Onboarding, OnboardingActivity } from '../types/onboarding';

/**
 * Activity item for dashboard display
 */
export interface DashboardActivity {
  id: string;
  type: 'lead' | 'demo' | 'proposal' | 'onboarding' | 'contract';
  title: string;
  description: string;
  timestamp: Date;
  user?: string;
  priority?: 'high' | 'medium' | 'low';
  resourceId?: string;
  resourceType?: string;
}

/**
 * Task item for dashboard display
 */
export interface DashboardTask {
  id: string;
  title: string;
  dueDate: Date;
  dueIn: string;
  priority: 'high' | 'medium' | 'low';
  resourceId: string;
  resourceType: 'lead' | 'onboarding';
  status?: string;
}

/**
 * Format relative time (e.g., "2 hours", "Tomorrow")
 */
function formatDueIn(date: Date): string {
  const now = new Date();
  const diff = date.getTime() - now.getTime();
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  if (hours < 0) {
    return 'Overdue';
  } else if (hours < 1) {
    return 'Less than 1 hour';
  } else if (hours < 24) {
    return `${hours} hour${hours === 1 ? '' : 's'}`;
  } else if (days === 1) {
    return 'Tomorrow';
  } else if (days < 7) {
    return `${days} day${days === 1 ? '' : 's'}`;
  } else {
    return date.toLocaleDateString();
  }
}

/**
 * Get priority based on due date
 */
function getPriority(dueDate: Date): 'high' | 'medium' | 'low' {
  const now = new Date();
  const diff = dueDate.getTime() - now.getTime();
  const hours = Math.floor(diff / (1000 * 60 * 60));

  if (hours < 24) return 'high';
  if (hours < 72) return 'medium';
  return 'low';
}

/**
 * Map lead activity type to dashboard activity type
 */
function mapActivityType(type: string, status?: string): DashboardActivity['type'] {
  switch (type) {
    case 'demo':
      return 'demo';
    case 'proposal':
      return 'proposal';
    case 'call':
    case 'email':
    case 'meeting':
    case 'note':
    default:
      return 'lead';
  }
}

/**
 * Get recent activities from leads and onboardings
 */
export async function getRecentActivities(limitCount: number = 10): Promise<DashboardActivity[]> {
  const activities: DashboardActivity[] = [];

  try {
    // Get recent lead activities
    const leadsQuery = query(
      collection(db, 'leads'),
      orderBy('updatedAt', 'desc'),
      firestoreLimit(20)
    );
    const leadsSnapshot = await getDocs(leadsQuery);

    leadsSnapshot.docs.forEach((doc) => {
      const lead = { id: doc.id, ...doc.data() } as Lead;
      
      // Extract activities from lead
      if (lead.activities && Array.isArray(lead.activities)) {
        lead.activities.forEach((activity: LeadActivity) => {
          if (activity && activity.timestamp) {
            activities.push({
              id: `lead-${lead.id}-${activity.id}`,
              type: mapActivityType(activity.type, lead.status),
              title: activity.subject || `${activity.type.charAt(0).toUpperCase() + activity.type.slice(1)} Activity`,
              description: `${lead.contactInfo.company || lead.contactInfo.name} - ${lead.businessInfo.doorCount} doors`,
              timestamp: activity.timestamp instanceof Timestamp 
                ? activity.timestamp.toDate() 
                : new Date(activity.timestamp),
              user: activity.performedByName || activity.performedBy,
              priority: lead.status === 'demo_scheduled' || lead.status === 'negotiating' ? 'high' : 'medium',
              resourceId: lead.id,
              resourceType: 'lead',
            });
          }
        });
      }

      // Also add lead status changes as activities
      if (lead.updatedAt) {
        const updatedAt = lead.updatedAt instanceof Timestamp 
          ? lead.updatedAt.toDate() 
          : new Date(lead.updatedAt);
        
        // Check if this is a recent update (within last 24 hours)
        const dayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
        if (updatedAt > dayAgo) {
          const statusDisplay = {
            'new': 'New Lead',
            'qualifying': 'Lead Qualifying',
            'demo_scheduled': 'Demo Scheduled',
            'demo_completed': 'Demo Completed',
            'proposal_sent': 'Proposal Sent',
            'negotiating': 'In Negotiation',
            'closed_won': 'Deal Won!',
            'closed_lost': 'Deal Lost',
          };

          activities.push({
            id: `lead-status-${lead.id}`,
            type: lead.status === 'demo_scheduled' ? 'demo' : 
                  lead.status === 'proposal_sent' ? 'proposal' : 'lead',
            title: statusDisplay[lead.status] || lead.status,
            description: `${lead.contactInfo.company || lead.contactInfo.name} - ${lead.businessInfo.doorCount} doors`,
            timestamp: updatedAt,
            user: lead.assignedToName || lead.assignedTo,
            priority: ['demo_scheduled', 'proposal_sent', 'negotiating', 'closed_won'].includes(lead.status) 
              ? 'high' : 'medium',
            resourceId: lead.id,
            resourceType: 'lead',
          });
        }
      }
    });

    // Get recent onboarding activities
    const onboardingsQuery = query(
      collection(db, 'onboardings'),
      orderBy('updatedAt', 'desc'),
      firestoreLimit(10)
    );
    const onboardingsSnapshot = await getDocs(onboardingsQuery);

    onboardingsSnapshot.docs.forEach((doc) => {
      const onboarding = { id: doc.id, ...doc.data() } as Onboarding;
      
      // Extract activities from onboarding
      if (onboarding.activities && Array.isArray(onboarding.activities)) {
        onboarding.activities.slice(-5).forEach((activity: OnboardingActivity) => {
          if (activity && activity.timestamp) {
            activities.push({
              id: `onboarding-${onboarding.id}-${activity.id || Date.now()}`,
              type: 'onboarding',
              title: activity.description || 'Onboarding Update',
              description: `${onboarding.companyName} - Onboarding in progress`,
              timestamp: activity.timestamp instanceof Timestamp 
                ? activity.timestamp.toDate() 
                : new Date(activity.timestamp),
              user: activity.performedByName || activity.performedBy,
              priority: onboarding.status === 'blocked' ? 'high' : 'medium',
              resourceId: onboarding.id,
              resourceType: 'onboarding',
            });
          }
        });
      }
    });

    // Sort by timestamp descending and limit
    activities.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
    return activities.slice(0, limitCount);
  } catch (error) {
    console.error('Error fetching recent activities:', error);
    return [];
  }
}

/**
 * Get upcoming tasks from leads (follow-ups) and onboardings
 */
export async function getUpcomingTasks(limitCount: number = 10): Promise<DashboardTask[]> {
  const tasks: DashboardTask[] = [];
  const now = new Date();
  const nextWeek = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

  try {
    // Get leads with upcoming follow-ups
    const leadsQuery = query(
      collection(db, 'leads'),
      where('status', 'not-in', ['closed_won', 'closed_lost']),
      orderBy('nextFollowupDate', 'asc'),
      firestoreLimit(20)
    );
    
    try {
      const leadsSnapshot = await getDocs(leadsQuery);

      leadsSnapshot.docs.forEach((doc) => {
        const lead = { id: doc.id, ...doc.data() } as Lead;
        
        if (lead.nextFollowupDate) {
          const dueDate = lead.nextFollowupDate instanceof Timestamp 
            ? lead.nextFollowupDate.toDate() 
            : new Date(lead.nextFollowupDate);

          // Only include tasks due within the next week
          if (dueDate <= nextWeek) {
            tasks.push({
              id: `lead-followup-${lead.id}`,
              title: `Follow up with ${lead.contactInfo.company || lead.contactInfo.name}`,
              dueDate,
              dueIn: formatDueIn(dueDate),
              priority: getPriority(dueDate),
              resourceId: lead.id,
              resourceType: 'lead',
              status: lead.status,
            });
          }
        }

        // Also check for scheduled demos
        if (lead.demoScheduledFor) {
          const demoDate = lead.demoScheduledFor instanceof Timestamp 
            ? lead.demoScheduledFor.toDate() 
            : new Date(lead.demoScheduledFor);

          if (demoDate >= now && demoDate <= nextWeek) {
            tasks.push({
              id: `lead-demo-${lead.id}`,
              title: `Demo with ${lead.contactInfo.company || lead.contactInfo.name}`,
              dueDate: demoDate,
              dueIn: formatDueIn(demoDate),
              priority: 'high',
              resourceId: lead.id,
              resourceType: 'lead',
              status: 'demo_scheduled',
            });
          }
        }
      });
    } catch (leadError) {
      // Handle index not ready error gracefully
      console.warn('Lead followup query failed, trying simpler query:', leadError);
      
      // Fallback: get all active leads without the compound query
      const fallbackQuery = query(
        collection(db, 'leads'),
        firestoreLimit(30)
      );
      const fallbackSnapshot = await getDocs(fallbackQuery);

      fallbackSnapshot.docs.forEach((doc) => {
        const lead = { id: doc.id, ...doc.data() } as Lead;
        
        if (['closed_won', 'closed_lost'].includes(lead.status)) return;

        if (lead.nextFollowupDate) {
          const dueDate = lead.nextFollowupDate instanceof Timestamp 
            ? lead.nextFollowupDate.toDate() 
            : new Date(lead.nextFollowupDate);

          if (dueDate <= nextWeek) {
            tasks.push({
              id: `lead-followup-${lead.id}`,
              title: `Follow up with ${lead.contactInfo.company || lead.contactInfo.name}`,
              dueDate,
              dueIn: formatDueIn(dueDate),
              priority: getPriority(dueDate),
              resourceId: lead.id,
              resourceType: 'lead',
              status: lead.status,
            });
          }
        }

        if (lead.demoScheduledFor) {
          const demoDate = lead.demoScheduledFor instanceof Timestamp 
            ? lead.demoScheduledFor.toDate() 
            : new Date(lead.demoScheduledFor);

          if (demoDate >= now && demoDate <= nextWeek) {
            tasks.push({
              id: `lead-demo-${lead.id}`,
              title: `Demo with ${lead.contactInfo.company || lead.contactInfo.name}`,
              dueDate: demoDate,
              dueIn: formatDueIn(demoDate),
              priority: 'high',
              resourceId: lead.id,
              resourceType: 'lead',
              status: 'demo_scheduled',
            });
          }
        }
      });
    }

    // Get onboardings that need attention
    const onboardingsQuery = query(
      collection(db, 'onboardings'),
      where('status', 'in', ['in_progress', 'blocked']),
      firestoreLimit(10)
    );
    
    try {
      const onboardingsSnapshot = await getDocs(onboardingsQuery);

      onboardingsSnapshot.docs.forEach((doc) => {
        const onboarding = { id: doc.id, ...doc.data() } as Onboarding;
        
        // Add blocked onboardings as high priority tasks
        if (onboarding.status === 'blocked') {
          tasks.push({
            id: `onboarding-blocked-${onboarding.id}`,
            title: `Resolve blocker for ${onboarding.companyName}`,
            dueDate: now, // Immediate attention needed
            dueIn: 'ASAP',
            priority: 'high',
            resourceId: onboarding.id,
            resourceType: 'onboarding',
            status: 'blocked',
          });
        }

        // Check for target completion date
        if (onboarding.targetCompletionDate) {
          const completionDate = onboarding.targetCompletionDate instanceof Timestamp 
            ? onboarding.targetCompletionDate.toDate() 
            : new Date(onboarding.targetCompletionDate);

          if (completionDate <= nextWeek) {
            tasks.push({
              id: `onboarding-completion-${onboarding.id}`,
              title: `Complete onboarding for ${onboarding.companyName}`,
              dueDate: completionDate,
              dueIn: formatDueIn(completionDate),
              priority: 'high',
              resourceId: onboarding.id,
              resourceType: 'onboarding',
              status: onboarding.status,
            });
          }
        }
      });
    } catch (onboardingError) {
      console.warn('Onboarding query failed:', onboardingError);
    }

    // Sort by due date ascending and limit
    tasks.sort((a, b) => a.dueDate.getTime() - b.dueDate.getTime());
    return tasks.slice(0, limitCount);
  } catch (error) {
    console.error('Error fetching upcoming tasks:', error);
    return [];
  }
}

/**
 * Get dashboard summary statistics
 */
export interface DashboardSummary {
  totalActiveLeads: number;
  demosThisWeek: number;
  proposalsPending: number;
  activeOnboardings: number;
  blockedOnboardings: number;
  recentWins: number;
}

export async function getDashboardSummary(): Promise<DashboardSummary> {
  const now = new Date();
  const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  const nextWeek = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

  let summary: DashboardSummary = {
    totalActiveLeads: 0,
    demosThisWeek: 0,
    proposalsPending: 0,
    activeOnboardings: 0,
    blockedOnboardings: 0,
    recentWins: 0,
  };

  try {
    // Get leads stats
    const leadsSnapshot = await getDocs(collection(db, 'leads'));
    
    leadsSnapshot.docs.forEach((doc) => {
      const lead = doc.data() as Lead;
      
      // Count active leads
      if (!['closed_won', 'closed_lost'].includes(lead.status)) {
        summary.totalActiveLeads++;
      }

      // Count proposals pending
      if (lead.status === 'proposal_sent' || lead.status === 'negotiating') {
        summary.proposalsPending++;
      }

      // Count demos this week
      if (lead.demoScheduledFor) {
        const demoDate = lead.demoScheduledFor instanceof Timestamp 
          ? lead.demoScheduledFor.toDate() 
          : new Date(lead.demoScheduledFor);
        
        if (demoDate >= now && demoDate <= nextWeek) {
          summary.demosThisWeek++;
        }
      }

      // Count recent wins (last 7 days)
      if (lead.status === 'closed_won' && lead.closedAt) {
        const closedDate = lead.closedAt instanceof Timestamp 
          ? lead.closedAt.toDate() 
          : new Date(lead.closedAt);
        
        if (closedDate >= weekAgo) {
          summary.recentWins++;
        }
      }
    });

    // Get onboarding stats
    const onboardingsSnapshot = await getDocs(collection(db, 'onboardings'));
    
    onboardingsSnapshot.docs.forEach((doc) => {
      const onboarding = doc.data() as Onboarding;
      
      if (onboarding.status === 'in_progress') {
        summary.activeOnboardings++;
      }
      
      if (onboarding.status === 'blocked') {
        summary.blockedOnboardings++;
      }
    });

    return summary;
  } catch (error) {
    console.error('Error fetching dashboard summary:', error);
    return summary;
  }
}
